/**
 * AdminAppConfig - Control de Módulos GOATGYM
 * 
 * Panel para activar/desactivar secciones de la app por rol.
 * Permite configurar qué funcionalidades están disponibles para Free/Premium/Admin.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  Settings,
  Loader2,
  RotateCcw,
  Save,
  Utensils,
  Apple,
  Camera,
  TrendingUp,
  Video,
  Dumbbell,
  Crown,
  LayoutDashboard,
  History,
  Pencil,
  Users,
  Shield,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeo de iconos
const ICON_MAP = {
  utensils: Utensils,
  apple: Apple,
  camera: Camera,
  "trending-up": TrendingUp,
  video: Video,
  dumbbell: Dumbbell,
  crown: Crown,
  "layout-dashboard": LayoutDashboard,
  history: History,
};

const AdminAppConfig = () => {
  const { getAuthHeaders } = useAuth();
  
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editMessage, setEditMessage] = useState("");

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/app-config/modules`, {
        headers: getAuthHeaders(),
      });
      setModules(response.data.modules || []);
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = async (moduleId, role) => {
    setSaving(`${moduleId}-${role}`);
    try {
      await axios.post(
        `${API_URL}/api/admin/app-config/modules/${moduleId}/toggle?role=${role}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Actualizar estado local
      setModules(prev => prev.map(m => {
        if (m.id === moduleId) {
          return { ...m, [`enabled_${role}`]: !m[`enabled_${role}`] };
        }
        return m;
      }));
      
      toast.success(`Módulo actualizado para ${role}`);
    } catch (error) {
      console.error("Error toggling module:", error);
      toast.error("Error al actualizar módulo");
    } finally {
      setSaving(null);
    }
  };

  const updateModuleMessage = async () => {
    if (!editingModule) return;
    
    setSaving("message");
    try {
      await axios.put(
        `${API_URL}/api/admin/app-config/modules/${editingModule.id}`,
        { coming_soon_message: editMessage },
        { headers: getAuthHeaders() }
      );
      
      // Actualizar estado local
      setModules(prev => prev.map(m => {
        if (m.id === editingModule.id) {
          return { ...m, coming_soon_message: editMessage };
        }
        return m;
      }));
      
      toast.success("Mensaje actualizado");
      setEditingModule(null);
    } catch (error) {
      console.error("Error updating message:", error);
      toast.error("Error al actualizar mensaje");
    } finally {
      setSaving(null);
    }
  };

  const resetConfig = async () => {
    if (!window.confirm("¿Restablecer toda la configuración a valores por defecto?")) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/app-config/reset`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Configuración restablecida");
      fetchModules();
    } catch (error) {
      console.error("Error resetting config:", error);
      toast.error("Error al restablecer");
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Settings;
    return <IconComponent className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-app-config">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white flex items-center gap-3">
            <Settings className="w-8 h-8 text-[#D4AF37]" />
            Control de Módulos
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Activa o desactiva secciones de la app por tipo de usuario
          </p>
        </div>
        
        <Button 
          onClick={resetConfig}
          variant="outline"
          className="border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Restablecer
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 p-4 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400">Free</span>
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-sm text-[#D4AF37]">Premium</span>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-purple-400">Admin</span>
        </div>
      </div>

      {/* Modules Grid */}
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto" />
          <p className="text-muted-foreground mt-2">Cargando configuración...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {modules.map((module) => (
            <Card 
              key={module.id} 
              className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#D4AF37]/30 transition-colors"
              data-testid={`module-card-${module.id}`}
            >
              <CardContent className="p-4">
                {/* Module Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37]">
                      {getIcon(module.icon)}
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{module.name}</h3>
                      <p className="text-xs text-muted-foreground">{module.description}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingModule(module);
                      setEditMessage(module.coming_soon_message || "");
                    }}
                    className="h-8 w-8 text-muted-foreground hover:text-white"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>

                {/* Toggles */}
                <div className="grid grid-cols-3 gap-4">
                  {/* Free */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-400">Free</span>
                    </div>
                    <Switch
                      checked={module.enabled_free}
                      onCheckedChange={() => toggleModule(module.id, "free")}
                      disabled={saving === `${module.id}-free`}
                      data-testid={`toggle-${module.id}-free`}
                    />
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px]",
                        module.enabled_free 
                          ? "bg-green-500/10 text-green-400 border-green-500/30" 
                          : "bg-red-500/10 text-red-400 border-red-500/30"
                      )}
                    >
                      {module.enabled_free ? "ON" : "OFF"}
                    </Badge>
                  </div>

                  {/* Premium */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-[#D4AF37]" />
                      <span className="text-xs text-[#D4AF37]">Premium</span>
                    </div>
                    <Switch
                      checked={module.enabled_premium}
                      onCheckedChange={() => toggleModule(module.id, "premium")}
                      disabled={saving === `${module.id}-premium`}
                      data-testid={`toggle-${module.id}-premium`}
                    />
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px]",
                        module.enabled_premium 
                          ? "bg-green-500/10 text-green-400 border-green-500/30" 
                          : "bg-red-500/10 text-red-400 border-red-500/30"
                      )}
                    >
                      {module.enabled_premium ? "ON" : "OFF"}
                    </Badge>
                  </div>

                  {/* Admin */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3 h-3 text-purple-400" />
                      <span className="text-xs text-purple-400">Admin</span>
                    </div>
                    <Switch
                      checked={module.enabled_admin}
                      onCheckedChange={() => toggleModule(module.id, "admin")}
                      disabled={saving === `${module.id}-admin`}
                      data-testid={`toggle-${module.id}-admin`}
                    />
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px]",
                        module.enabled_admin 
                          ? "bg-green-500/10 text-green-400 border-green-500/30" 
                          : "bg-red-500/10 text-red-400 border-red-500/30"
                      )}
                    >
                      {module.enabled_admin ? "ON" : "OFF"}
                    </Badge>
                  </div>
                </div>

                {/* Coming Soon Message Preview */}
                <div className="mt-4 pt-3 border-t border-[#2A2A2A]">
                  <p className="text-[10px] text-muted-foreground mb-1">Mensaje cuando está desactivado:</p>
                  <p className="text-xs text-yellow-500/80 italic truncate">
                    "{module.coming_soon_message}"
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Message Dialog */}
      <Dialog open={!!editingModule} onOpenChange={() => setEditingModule(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#D4AF37]" />
              Editar mensaje: {editingModule?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-white">Mensaje "Próximamente"</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Este mensaje se mostrará cuando el módulo esté desactivado
              </p>
              <Input
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                placeholder="Esta sección estará disponible próximamente"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEditingModule(null)}
                className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
              >
                Cancelar
              </Button>
              <Button
                onClick={updateModuleMessage}
                disabled={saving === "message"}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
              >
                {saving === "message" ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppConfig;
